import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, type FC } from "react";
import { Seo } from "src/components/seo";
import { RouterLink } from "src/components/router-link";
import { usePageView } from "src/hooks/use-page-view";
import i18next from "src/i18n";
import { paths } from "src/paths";

const PAGE_URL = "https://www.opensource.clinic/nl/diabetes-coaching";
const BOOK_HREF = "https://cal.com/team/opensourcelinic/medical-coaching";
const BIG_HREF =
  "https://zoeken.bigregister.nl/zorgverlener/ea35cb3f-53a3-4247-b9f1-7e47e4692827";

const SEO_TITLE = "Diabetescoach Nederland | Persoonlijke Diabetesbegeleiding";
const SEO_DESCRIPTION =
  "Zoek je begeleiding bij diabetes en open-source technologie? Ontdek persoonlijke coaching, hulp bij Nightscout, AndroidAPS en DIY Looping.";

const audiences = [
  {
    title: "Je loopt al en het loopt niet soepel",
    body: "Basaal, insulinegevoeligheid, koolhydraatratio of dynamische ISF sluiten niet aan op je dagen. Je wilt iemand die de grafiek én de instellingen kan lezen.",
  },
  {
    title: "Je wilt starten met een open-source loop",
    body: "AndroidAPS, Loop of Trio staat op je telefoon, of je wilt daarmee beginnen. De wiki is lang en je zoekt begeleiding bij de eerste werkende opzet.",
  },
  {
    title: "Ouders die willen meekijken",
    body: "Nightscout moet kloppen voor wie op afstand volgt: leesrechten, alarmen en een dashboard instellingen die helpen.",
  },
  {
    title: "Diabetescoach online met technische expertise",
    body: "Je  wilt geen algemeen programma, maar gesprekken over hoe je technologie kan aanpassen aan jouw leven. Online en wanneer het past binnen jouw tijdschema.",
  },
];

const systems = [
  {
    title: "AndroidAPS",
    body: "Draait op een Android-telefoon. De eerste testversie startte in 2015, de eerste uitgave volgde in 2016. Meer dan 13.900 mensen gebruiken het.",
  },
  {
    title: "Loop",
    body: "Draait op een iPhone. De ontwikkeling startte in 2016. Meer dan 33.400 mensen gebruiken het.",
  },
  {
    title: "Trio",
    body: "Draait op een iPhone. Als zelfstandig project begon de ontwikkeling in 2023, voortbouwend op eerdere iPhone-apps. Enkele duizenden mensen gebruiken het.",
  },
  {
    title: "Nightscout",
    body: "Een dashboard in de browser, op telefoon of computer. De ontwikkeling begon in 2013, de open-source uitgave volgde in 2014. Meer dan 72.500 mensen gebruiken het.",
  },
];

const differences = [
  {
    title: "Diabetes, technologie en automatisering",
    body: "De coaching gaat over je glucoseregulatie samen met open-source software. Of je nu sporter bent of je houdt van een avondje popcorn met de familie, we passen de technologie zoveel mogelijk aan aan jouw leefstijl.",
  },
  {
    title: "Medische en technische expertise in één team",
    body: "Een BIG-geregistreerde zorgprofessional, data en software specialisten in één team. Mensen die open-source software zelf gebruiken.",
  },
  {
    title: "Een diabetescoach online, in heel Nederland",
    body: "Gesprekken gaan via video. Woon je in Nederland en heb je iemand nodig die de loop thuis aansluit, dan is een huisbezoek ook mogelijk.",
  },
  {
    title: "Je houdt zelf de regie",
    body: "De software is open source waardoor veel aanpasbaar is aan jouw leefsituatie.",
  },
];

const coaches = [
  {
    name: "Kelvin Kramp",
    role: "Arts, PhD",
    avatar: "/assets/avatars/KK.jpeg",
    qualifications: "BIG-geregistreerd arts. ",
    background:
      "Als arts stapte hij over naar health tech nadat hij in 2021 zelf met opensource loopen begon. Tijdens begeleiding gebruikt hij zijn medische en onderzoeks achtergrond voor het optimaliseren van app instellingen, profielen en de verbinding tussen sensoren en pompen.",
    links: [
      {
        label: "Achtergrond",
        href: "/blog/details/Introducing-Kelvin-Kramp",
        external: false,
      },
      { label: "BIG-register", href: BIG_HREF, external: true },
    ],
  },
  {
    name: "Lukáš Trčka",
    role: "Diabeteseducator",
    avatar: "/assets/avatars/LukasTrcka.png",
    qualifications:
      "Type 1-diabetes sinds 2003. Actief met AndroidAPS sinds 2017.",
    background:
      "Actief in de open source community sinds 2017. Hij geeft internationale workshops aan patienten en medisch specialisten over open-source software gebruik bij diabetes. In zijn vrije tijd geeft hij begeleiding aan mensen die starten of vastlopen met open-source looping. ",
    links: [
      {
        label: "Achtergrond",
        href: `/blog/details/${encodeURIComponent(
          "Introducing Lukáš Trčka".replace(/\s+/g, "-")
        )}`,
        external: false,
      },
      {
        label: "LinkedIn",
        href: "https://cz.linkedin.com/in/lukastrcka",
        external: true,
      },
    ],
  },
  {
    name: "Marvin Micka",
    role: "Diabetescoach",
    avatar: "/assets/avatars/marvinmicka.png",
    qualifications: "Diabetescoach bij Opensource.clinic.",
    background:
      "Sinds begin van de Nederlandse Facebook voor loopen actief in de diabetes community. Hij begeleidt mensen die hun diabetes willen afstemmen op open-source technologie. ",
    links: [
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/marvin-micka-80b3ab71/",
        external: true,
      },
    ],
  },
];

const faqs = [
  {
    question: "Wat doet een diabetescoach bij Opensource.clinic?",
    answer:
      "Je krijgt persoonlijke begeleiding bij diabetesmanagement met open-source technologie. Dat gaat over AndroidAPS, Loop, Trio en Nightscout: de opzet, de instellingen en de vragen die ontstaan als automatisering en je glucosepatroon niet op elkaar aansluiten.",
  },
  {
    question: "Is dit een leefstijlcoach of een diëtist?",
    answer:
      "Nee. Deze diabetescoaching gaat over diabetes, technologie en open-source automatisering. Voedingsschema's en standaard leefstijladvies vallen daar buiten.",
  },
  {
    question: "Helpen jullie met AndroidAPS, Loop, Trio en Nightscout?",
    answer:
      "Ja. We helpen bij kiezen, inrichten en afstellen van deze systemen, en bij technische fouten die de loop of Nightscout in de weg zitten.",
  },
  {
    question: "Kan ik in Nederland online een diabetescoach spreken?",
    answer:
      "Ja. De gesprekken zijn online, vanuit heel Nederland. Voor de technische opzet van je loop is een huisbezoek in Nederland mogelijk. Begeleiding is er in het Nederlands, Engels en Tsjechisch.",
  },
  {
    question:
      "Vervangt deze begeleiding mijn internist of diabetesverpleegkundige?",
    answer:
      "Nee. De coaching staat naast je eigen behandeling. We nemen de coördinatie van je zorg niet over en de medische eindverantwoordelijkheid blijft bij je behandelaar.",
  },
  {
    question: "Wat gebeurt er in het gratis kennismakingsgesprek?",
    answer:
      "We bekijken je situatie, je systeem en je vraag. Daarna hoor je of begeleiding past en welk traject daarbij hoort. Het gesprek is gratis en vrijblijvend.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": PAGE_URL,
      url: PAGE_URL,
      name: SEO_TITLE,
      description: SEO_DESCRIPTION,
      inLanguage: "nl-NL",
      isPartOf: {
        "@type": "WebSite",
        name: "Opensource.clinic",
        url: "https://www.opensource.clinic",
      },
    },
    {
      "@type": "Service",
      name: "Persoonlijke diabetescoaching",
      serviceType: "Diabetescoaching voor open-source technologie",
      url: PAGE_URL,
      description: SEO_DESCRIPTION,
      areaServed: {
        "@type": "Country",
        name: "Netherlands",
      },
      availableLanguage: ["nl", "en", "cs"],
      provider: {
        "@type": "Organization",
        name: "Opensource.clinic",
        url: "https://www.opensource.clinic",
        address: {
          "@type": "PostalAddress",
          addressCountry: "NL",
        },
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

const sectionTitleSx = {
  fontWeight: 700,
  letterSpacing: "-0.02em",
  mb: 1.5,
} as const;

const DiabetesCoachingNlPage: FC = () => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  usePageView();

  useEffect(() => {
    void i18next.changeLanguage("nl");
    localStorage.setItem("language", "nl");
  }, []);

  return (
    <>
      <Seo
        title={SEO_TITLE}
        exactTitle
        description={SEO_DESCRIPTION}
        keywords="diabetes coach Nederland, diabetescoaching, diabetes begeleiding, diabetes technologie, diabetescoach online"
        canonical={PAGE_URL}
        type="website"
        htmlLang="nl"
        structuredData={structuredData}
        hreflang={[
          { lang: "nl", url: PAGE_URL },
          { lang: "x-default", url: PAGE_URL },
        ]}
      />

      <Box component="main" sx={{ bgcolor: "background.paper" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pt: { xs: 12, md: 16 },
            pb: { xs: 6, md: 8 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "primary.main",
                  mb: 1.5,
                  display: "block",
                }}
              >
                Diabetescoaching in Nederland
              </Typography>
              <Typography
                component="h1"
                variant="h1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.12,
                  fontSize: smDown ? "2rem" : "3.25rem",
                }}
              >
                Diabetescoach voor mensen die meer uit hun diabetesmanagement
                willen halen
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 2.5,
                  maxWidth: 680,
                  fontSize: smDown ? "1rem" : "1.125rem",
                  lineHeight: 1.7,
                  color: "text.secondary",
                }}
              >
                Zoek je een diabetes coach in Nederland voor AndroidAPS, Loop,
                Trio of Nightscout? Opensource.clinic geeft persoonlijke
                diabetesbegeleiding aan mensen die hun open-source systeem
                willen starten, herstellen of fijner afstellen.
              </Typography>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{ mt: 4, alignItems: { xs: "stretch", md: "center" } }}
              >
                <Button
                  component="a"
                  href={BOOK_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 999, px: 3, py: 1.25 }}
                >
                  Plan een gratis kennismakingsgesprek
                </Button>
                <Button
                  component={RouterLink}
                  href={paths.medicalCoaching}
                  variant="outlined"
                  size="large"
                  sx={{ borderRadius: 999, px: 3, py: 1.25 }}
                >
                  Bekijk de coachingtrajecten
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Container maxWidth="lg" sx={{ py: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1.5, sm: 4 }}
              sx={{ justifyContent: "space-between" }}
            >
              {[
                "Online in heel Nederland",
                "AndroidAPS, Loop, Trio en Nightscout",
                "Medische en technische expertise",
                "Gratis kennismakingsgesprek",
              ].map((label) => (
                <Typography
                  key={label}
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.secondary" }}
                >
                  {label}
                </Typography>
              ))}
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            Professionele diabetesbegeleiding in Nederland
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", maxWidth: 760, lineHeight: 1.75 }}
          >
            Opensource loopen geeft je een systeem dat meedenkt, maar alleen als
            sensor, pomp, app en instellingen goed op elkaar zijn afgestemd.
            Veel mensen in Nederland redden de installatie met de documentatie
            en de community. Anderen blijven hangen foutmelding, keuze stress,
            of instellingen die niet passen. Daar is deze diabetescoaching voor:
            concrete hulp bij jouw situatie, in het Nederlands.
          </Typography>
        </Container>

        <Box
          id="voor-wie"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "#F7F9FC",
            py: { xs: 6, md: 8 },
            scrollMarginTop: 96,
          }}
        >
          <Container maxWidth="lg">
            <Typography component="h2" variant="h3" sx={sectionTitleSx}>
              Voor wie is de coaching bedoeld?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 720,
                mb: { xs: 4, md: 5 },
                lineHeight: 1.7,
              }}
            >
              Voor mensen met diabetes, ouders en partners die meer uit hun
              open-source systeem willen halen. Een diabetescoach online, zonder
              dat je daarvoor naar een spreekuur hoeft.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              {audiences.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <Typography
                    component="h3"
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.7 }}
                  >
                    {item.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        <Container
          id="systemen"
          maxWidth="lg"
          sx={{ py: { xs: 6, md: 8 }, scrollMarginTop: 96 }}
        >
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            Hulp bij AndroidAPS, Loop, Trio en Nightscout
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 720,
              mb: { xs: 4, md: 5 },
              lineHeight: 1.7,
            }}
          >
            Elk app heeft zijn eigen voordelen en valkuilen. We werken met de
            apps die jij gebruikt, zodat je het maximale uit je loop haalt, of
            helpen je kiezen als je nog moet beginnen. Meer achtergrond staat op
            de pagina over{" "}
            <Link component={RouterLink} href={paths.whatIsopensourcelooping}>
              opensource loopen
            </Link>{" "}
            en bij{" "}
            <Link component={RouterLink} href={paths.nightscout}>
              Nightscout-hosting
            </Link>
            .
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {systems.map((system) => (
              <Box key={system.title}>
                <Typography
                  component="h3"
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  {system.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.7 }}
                >
                  {system.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>

        <Container
          id="persoonlijk"
          maxWidth="lg"
          sx={{ py: { xs: 6, md: 8 }, scrollMarginTop: 96 }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography component="h2" variant="h3" sx={sectionTitleSx}>
              Persoonlijke begeleiding door mensen met medische en technische
              expertise
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", lineHeight: 1.75, mb: 2 }}
            >
              Instellingen zijn persoonlijk. Basaal, insulinegevoeligheid,
              koolhydraatratio, werkingsduur insuline, tijdelijke doelen en
              overrides horen bij jouw dagen, niet bij een standaardprofiel. Een
              arts, ervaringsdeskundigen en een technisch team kijken met je mee
              naar patronen in je data, zodat het systeem past bij jouw leven.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", lineHeight: 1.75 }}
            >
              Dit is diabetes begeleiding naast je eigen behandelaar in het
              ziekenhuis of de huisarts praktijk. We nemen die behandeling niet
              over en geven geen advies dat losstaat van je loop. De concrete
              trajecten, van een korte configuratie tot langere begeleiding,
              staan op de{" "}
              <Link component={RouterLink} href={paths.medicalCoaching}>
                coachingpagina
              </Link>
              .
            </Typography>
          </Box>
        </Container>

        <Box
          id="onderscheid"
          sx={{
            bgcolor: "#F7F9FC",
            borderTop: 1,
            borderColor: "divider",
            py: { xs: 6, md: 8 },
            scrollMarginTop: 96,
          }}
        >
          <Container maxWidth="lg">
            <Typography component="h2" variant="h3" sx={sectionTitleSx}>
              Wat maakt Opensource.clinic anders?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 720,
                mb: { xs: 4, md: 5 },
                lineHeight: 1.7,
              }}
            >
              De meeste diabetescoaches werken vanuit voeding en leefstijl. Onze
              specialisatie is de combinatie van diabetes, technologie en
              open-source automatisering.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              }}
            >
              {differences.map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    p: 3,
                    borderTop: 3,
                    borderColor: "primary.main",
                  }}
                >
                  <Typography
                    component="h3"
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", lineHeight: 1.7 }}
                  >
                    {item.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        <Container
          id="coaches"
          maxWidth="lg"
          sx={{ py: { xs: 6, md: 8 }, scrollMarginTop: 96 }}
        >
          <Typography component="h2" variant="h3" sx={sectionTitleSx}>
            Wie zijn de coaches?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 720,
              mb: { xs: 4, md: 5 },
              lineHeight: 1.7,
            }}
          >
            Je spreekt mensen die de software kennen en die diabetes van
            dichtbij meemaken. Hieronder staan de coaches, met hun achtergrond
            en kwalificaties.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            }}
          >
            {coaches.map((coach) => (
              <Box
                key={coach.name}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Avatar
                  alt={coach.name}
                  src={coach.avatar}
                  sx={{ width: 96, height: 96, mb: 2 }}
                />
                <Typography
                  component="h3"
                  variant="h6"
                  sx={{ fontWeight: 700 }}
                >
                  {coach.name}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "primary.main", mb: 1.5, fontWeight: 700 }}
                >
                  {coach.role}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", lineHeight: 1.7, mb: 1 }}
                >
                  {coach.qualifications}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.7, mb: 2 }}
                >
                  {coach.background}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: "auto" }}>
                  {coach.links.map((item) =>
                    item.external ? (
                      <Link
                        key={item.label}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <Link
                        key={item.label}
                        component={RouterLink}
                        href={item.href}
                        underline="hover"
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        </Container>

        <Box
          id="faq"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "#F7F9FC",
            py: { xs: 6, md: 8 },
            scrollMarginTop: 96,
          }}
        >
          <Container maxWidth="md">
            <Typography component="h2" variant="h3" sx={sectionTitleSx}>
              Veelgestelde vragen
            </Typography>
            <Box sx={{ mt: 2 }}>
              {faqs.map((faq) => (
                <Accordion
                  key={faq.question}
                  disableGutters
                  elevation={0}
                  sx={{
                    bgcolor: "transparent",
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      component="h3"
                      variant="subtitle1"
                      sx={{ fontWeight: 600 }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.7 }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Box
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: "divider",
              p: { xs: 3, md: 5 },
              display: "grid",
              gap: 3,
              gridTemplateColumns: { xs: "1fr", md: "1.4fr 0.8fr" },
              alignItems: "center",
            }}
          >
            <Box>
              <Typography component="h2" variant="h3" sx={sectionTitleSx}>
                Plan een gratis kennismakingsgesprek
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", lineHeight: 1.7 }}
              >
                Vertel welk systeem je gebruikt en waar je op vastloopt. We
                kijken of diabetescoaching past en welk traject daarbij hoort.
                Het kennismakingsgesprek is gratis en vrijblijvend.
              </Typography>
            </Box>
            <Button
              component="a"
              href={BOOK_HREF}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 999, px: 3, py: 1.5 }}
            >
              Plan een gratis kennismakingsgesprek
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default DiabetesCoachingNlPage;
